import { CardOverlayLink } from '@/shared/components/listings/CardOverlayLink';
import { SaveButton } from '@/shared/components/listings/SaveButton';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { buildEstateUrl } from '@/shared/lib/estates/url';
import type { EstateCardData } from '@/shared/lib/types/estates';
import { plaintextString } from '@letsgethome/types/branded';
import { CardControlLayer, CardSurface } from '@letsgethome/ui/card-layers';

type Props = {
  estate: EstateCardData;
};

// Decorative placeholder glyph shown when an estate has no image. Not user
// copy (a building pictograph), so it's held as a constant rather than JSX text
// — keeps it out of i18next/no-literal-string without lint suppression.
const BUILDING_PLACEHOLDER_ICON = plaintextString('🏢');

/**
 * The estate index card — the SAME shape as `ListingCard` (LET-85).
 *
 * Until LET-85 this card was one `<Link>` WRAPPING everything, carousel arrows
 * and save heart included. That was not the stretched-link/z-order defect the
 * issue described (no z-index was ever in play here) — it was worse-formed:
 * a `<button>` inside an `<a>` is interactive content inside interactive
 * content, which the HTML content model forbids, and the photo attribution's
 * own `<a>` was an anchor inside an anchor. The controls only worked because
 * each one called `stopPropagation()` + `preventDefault()` itself.
 *
 * Now the root is the seam's own `CardSurface`, the link is a stretched
 * SIBLING on the overlay layer (`CardOverlayLink`), and every control sits on
 * the controls layer (`CardControlLayer`). No
 * anchor is an ancestor of any button, and which layer wins is the card seam's
 * tested claim rather than a per-card `z-*`.
 */
export function EstateCard({ estate }: Props) {
  const t = useTranslations('estates');

  return (
    // `CardSurface` carries the `relative` and the `group` — here, not on the
    // link: every `group-hover:` treatment below (the title colour) keys off
    // the whole card, and the link covers the whole card, so
    // hovering it hovers this root.
    //
    // No `overflow-hidden` on the root: the overlay link's focus ring is
    // offset OUTSIDE the card, and a clipping root would swallow it. The image
    // frame clips its own top corners instead.
    <CardSurface className="bg-card rounded-xl shadow-sm hover:shadow-md transition-shadow">
      <CardOverlayLink
        href={buildEstateUrl(estate.slug, estate.publicId)}
        aria-label={estate.name}
        className="rounded-xl"
      />

      {/* `aspect-[4/3]` = the Rust SSOT `cardImageAspectRatio()` (build-time
          Tailwind string; welded by card-aspect.weld.test.ts). */}
      <div className="relative aspect-[4/3] overflow-hidden rounded-t-xl">
        {/* D14 (wire epoch 43): the card carries NO photo. Google Places
            photos are fetched per VIEW and stored nowhere (the terms permit
            storing only the place id), which a directory page of cards cannot
            afford; the estate's own imagery (#7690) restores card photos. */}
        <div className="w-full h-full bg-muted flex items-center justify-center">
          <span className="text-4xl">{BUILDING_PLACEHOLDER_ICON}</span>
        </div>
        {estate.activeTotalCount > 0 && (
          <span className="absolute top-3 right-3 bg-success text-success-foreground text-xs font-medium px-2 py-1 rounded-full">
            {t('detail.listingCount', { count: estate.activeTotalCount })}
          </span>
        )}
        {/* Estate-level save heart (PR-B3) — top-LEFT because the listing-count
            badge owns the top-right corner. On the card seam's controls layer,
            beside the overlay link rather than inside it. */}
        <CardControlLayer className="left-3 top-3">
          <SaveButton saveable={{ kind: 'estate', id: estate.id }} serverSaved={estate.saved} />
        </CardControlLayer>
      </div>

      <div className="p-4">
        <h2 className="font-semibold text-foreground group-hover:text-success-text transition-colors line-clamp-1">
          {plaintextString(estate.name)}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{plaintextString(estate.districtName)}</p>
        {estate.streetAddress && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-1">
            {plaintextString(estate.streetAddress)}
          </p>
        )}
        {/* Rails index-card counts: blocks / for sale / for rent */}
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {estate.buildingsCount > 0 && (
            <span>{t('card.blocks', { count: estate.buildingsCount })}</span>
          )}
          {estate.activeSaleCount > 0 && (
            <span className="text-info-text">
              {t('card.forSale', { count: estate.activeSaleCount })}
            </span>
          )}
          {estate.activeRentCount > 0 && (
            <span className="text-success-text">
              {t('card.forRent', { count: estate.activeRentCount })}
            </span>
          )}
        </div>
      </div>
    </CardSurface>
  );
}
